import {Component, OnInit, ViewChild} from '@angular/core';
import {ArmTableComponent} from '../../../components/arm-table/arm-table.component';
import {DisplayedColumnInf} from '../../../interfaces/common/table/displayed-column-inf';
import {UserPrivilegeService} from '../../../services/common/user-privilege/user-privilege.service';
import {LoaderService} from '../../../services/common/loader-service/loader.service';
import {SipConditionGroupApiService} from '../../../services/api/settings/call-flow-configurations/sip-condition-group/sip-condition-group-api.service';
import {AcDialogService} from 'ac-infra';
import {SipConditionGroupsDialogComponent} from './sip-condition-groups-dialog/sip-condition-groups-dialog.component';
import {SnackBarService} from '../../../services/common/snack-bar/snack-bar.service';
import {ConfirmationDialogComponent} from '../../../dialogs/confirmation-dialog/confirmation-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import _ from 'lodash';
import {PrefixGroupsApiService} from '../../../services/api/settings/call-flow-configurations/prefix-groups/prefix-groups-api.service';
import Search from '../../../utils/search';
import {SipHeadersService} from '../../../services/common/sip-headers/sip-headers.service';
import {forkJoin} from 'rxjs';
import Formatting from '../../../utils/formatting';

@Component({
  selector: 'sip-condition-groups',
  templateUrl: './sip-condition-groups.component.html',
  styleUrls: ['./sip-condition-groups.component.scss']
})
export class SipConditionGroupsComponent implements OnInit {

  @ViewChild(ArmTableComponent) armTable;

  isUnauthorized: boolean;
  selection: any;
  displayedColumns: Array<DisplayedColumnInf> = [
    {name: 'name', display: 'Name', selected: true, width: 200},
    {name: 'conditionsDescription', display: 'Conditions', selected: true, width: 500}
  ];
  filter;
  items = [];
  headerStructure: any = [];
  operatorOptions: any = [];
  prefixGroupsOptions: any = [];

  headerOperators;

  constructor(private userPrivilege: UserPrivilegeService,
              private acDialogService: AcDialogService,
              private loaderService: LoaderService,
              private dialog: MatDialog,
              private snackBarService: SnackBarService,
              private sipHeadersService: SipHeadersService,
              private sipConditionGroupApiService: SipConditionGroupApiService,
              private prefixGroupsApiService: PrefixGroupsApiService) {
  }

  ngOnInit(): void {
    this.isUnauthorized = !this.userPrivilege.isAuthorized();
    this.refresh();
  }

  onFilter(filter: any): any {
    this.armTable.firstPage();
    this.filter = filter;
  }

  onSelection(selection: any): void {
    this.selection = selection;
  }

  addItem(): void {
    const dialogData: any = {
      data: {
        conditions: [{
          values: [],
          logicalOperator: 'and'
        }]
      },
      conditionGroup: {},
      headerStructure: this.headerStructure,
      headerOperators: this.headerOperators,
      operatorOptions: this.operatorOptions,
      prefixGroupsOptions: this.prefixGroupsOptions,
      isEditMode: false
    };

    this.acDialogService.open(SipConditionGroupsDialogComponent, {
      dialogData,
      title: 'Add Condition Group',
      onSubmit: () => {
        return this.sipConditionGroupApiService.addSipConditionGroup(dialogData.conditionGroup)
          .toPromise().then(() => {
            this.refresh();
            this.snackBarService.display(`${dialogData.data.name} successfully added`);
          });
      }
    });
  }

  editItem(): void {
    const dialogData: any = {
      data: _.cloneDeep(this.selection),
      conditionGroup: {},
      headerStructure: this.headerStructure,
      headerOperators: this.headerOperators,
      operatorOptions: this.operatorOptions,
      prefixGroupsOptions: this.prefixGroupsOptions,
      isEditMode: true
    };

    this.acDialogService.open(SipConditionGroupsDialogComponent, {
      dialogData,
      title: 'Edit Condition Group',
      onSubmit: () => {
        return this.sipConditionGroupApiService.editSipConditionGroup(dialogData.conditionGroup, dialogData.conditionGroup.id)
          .toPromise().then(() => {
            this.refresh();
            this.snackBarService.display(`${dialogData.data.name} successfully updated`);
          });
      }
    });
  }

  refresh(): void {
    this.sipHeadersService.getSipConditionTemplate().subscribe(sipConditionData => {
      this.headerStructure = sipConditionData.headerStructure;
      this.headerOperators = sipConditionData.headerOperators;
      this.operatorOptions = sipConditionData.conditionOps;

      forkJoin([
        this.prefixGroupsApiService.getPrefixGroupsSlim(),
        this.sipConditionGroupApiService.getSipConditionGroups()
      ]).subscribe(data => {
        this.prefixGroupsOptions = Search.generateSearchOptions(data[0].elements
          .filter(item => item.type === 'PREFIX'), 'name', 'id');
        this.items = data[1];
        this.items.forEach(item => {
          item.conditions.forEach(condition => {
            condition.displayData = {};

            if (condition.operator === 'belong') {
              condition.displayData.prefixGroups = condition.prefixGroups.map(pg => {
                return this.prefixGroupsOptions.find(pgo => {
                  return pgo.value === pg;
                });
              })?.map(pg => pg.text);
            }

            condition.displayData.operator = this.operatorOptions.find(operatorOption =>
              operatorOption.value === condition.operator)?.text;
          });

          item.conditionsDescription = this.generateConditionDescription(item.conditions);
        });
      });
    });
  }

  removeItem(): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: `Are you sure you want to delete the condition group: "${this.selection.name}" ?`
      }
    });

    dialogRef.componentInstance.onApply.subscribe(() => {
      this.sipConditionGroupApiService.deleteSipConditionGroup(this.selection.id).subscribe(() => {
        dialogRef.close();
        this.refresh();
        this.snackBarService.display(`${this.selection.name} successfully deleted`);
      });
    });
  }

  private generateConditionDescription(conditions: any): string {
    let conditionsTemplate = '';

    conditions.forEach((condition) => {
      conditionsTemplate += `${condition.subject} `;
      conditionsTemplate += `${condition.displayData?.operator} `;
      conditionsTemplate += `${this.getValues(condition)} `;
      conditionsTemplate += condition.logicalOperator ? `${condition.logicalOperator} ` : '';
    });

    return `${conditionsTemplate}`;
  }

  private getValues(condition): string {
    if (condition.operator === 'belong') {
      return condition.displayData.prefixGroups.join(', ');
    }

    return condition.values.map(value => Formatting.value(value)).join(' ,');
  }
}
